import { ApplicationConfig, provideZoneChangeDetection, inject, isDevMode, ErrorHandler } from '@angular/core';
import { provideRouter, withPreloading } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch,
} from '@angular/common/http';
import { routes } from './app.routes';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';

import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { provideApollo, APOLLO_NAMED_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client';
import type { DocumentNode } from 'graphql';

/** Apollo adds __typename to every query by default; skip that transform. */
class NoTypenameCache extends InMemoryCache {
  override transformDocument(document: DocumentNode): DocumentNode {
    return document;
  }
}
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { BackendRequestInterceptor } from './common/interceptors/backend-request-interceptor';
import { provideServiceWorker } from '@angular/service-worker';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import { AppSelectivePreloadingStrategy } from './common/util/app-selective-preloading.strategy';
import { createMfaRedirectErrorLink } from './common/util/mfa-error-link';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  // protectedResourceMap.set(
  //   environment.baseBackendUrl,
  //   [`${environment.msalConfig.auth.clientId}/.default`]
  // );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    }
  };
}

// Function to get the default language
export function getDefaultLanguage() {
  const availableLangs = ['en', 'es', 'fr', 'ja'];
  const savedLang = localStorage.getItem('selectedLanguage');
  const browserLang = navigator.language.split('-')[0];
  const resolved = savedLang || browserLang;
  return availableLangs.includes(resolved) ? resolved : 'en';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(AppSelectivePreloadingStrategy)),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BackendRequestInterceptor,
        multi: true,
    },
    {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink) {
        const mfaErrorLink = createMfaRedirectErrorLink();
        return {
          stationApp: {
            link: ApolloLink.from([mfaErrorLink, httpLink.create({ uri: environment.baseBackendUrl + 'graphql/StationApp' })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
              query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
            },
          },
          platform: {
            link: ApolloLink.from([mfaErrorLink, httpLink.create({ uri: environment.baseBackendUrl + 'graphql/Platform' })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
              query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
            },
          },
          trashPassApp: {
            link: ApolloLink.from([mfaErrorLink, httpLink.create({ uri: environment.baseBackendUrl + 'graphql/TrashPassApp' })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
              query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
            },
          },
          flushingStationApp: {
            link: ApolloLink.from([mfaErrorLink, httpLink.create({ uri: environment.baseBackendUrl + 'graphql/FlushingStationApp' })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
              query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
            },
          },
          stationStatusApp: {
            link: ApolloLink.from([mfaErrorLink, httpLink.create({ uri: environment.baseBackendUrl + 'graphql/StationStatusApp' })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
              query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
            },
          },
        };
      },
      deps: [HttpLink],
    },
    provideApollo(() => {
        const httpLink = inject(HttpLink);
        return {
            link: ApolloLink.from([createMfaRedirectErrorLink(), httpLink.create({
                uri: environment.baseBackendUrl + 'graphql/Platform',
            })]),
            cache: new NoTypenameCache(),
            defaultOptions: {
                query: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'all',
                },
            },
        };
    }), provideTransloco({
        config: { 
          availableLangs: ['en', 'es', 'fr', 'ja'],
          defaultLang: getDefaultLanguage(),
          // Remove this option if your application doesn't support changing language in runtime.
          reRenderOnLangChange: true,
          prodMode: !isDevMode(),
        },
        loader: TranslocoHttpLoader
      }), provideServiceWorker('ngsw-worker.js', {
            enabled: true,
            registrationStrategy: 'registerWhenStable:30000'
          })
],
};
